import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  type HeroSlideApi,
  adminFetchHeroSlides,
  adminSaveHero,
  adminDeleteHero,
  adminUploadHeroImage,
} from '@/api/client';
import { heroSlides as mockHeroSlides } from '@/mocks/heroSlides';

function mockToHeroApi(m: typeof mockHeroSlides[number], index: number): HeroSlideApi {
  return {
    id: -(index + 1),
    tag: m.tag,
    title: m.title,
    subtitle: m.subtitle,
    image: m.image,
    cta_primary: m.ctaPrimary,
    cta_secondary: m.ctaSecondary,
    sort_order: index + 1,
    active: 1,
  };
}

function normalizeActive(val: number | boolean): boolean {
  return val === 1 || val === true;
}

export default function AdminHeroManager() {
  const { t } = useTranslation('home');
  const [slides, setSlides] = useState<HeroSlideApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<HeroSlideApi>({
    id: 0,
    tag: '',
    title: '',
    subtitle: '',
    cta_primary: '',
    cta_secondary: '',
    image: '',
    sort_order: 1,
    active: 1,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadSlides = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setUsingMock(false);
    const result = await adminFetchHeroSlides();
    if (result.error) {
      setSlides(mockHeroSlides.map(mockToHeroApi));
      setFetchError(result.error);
      setUsingMock(true);
    } else if (result.data.length === 0) {
      setSlides(mockHeroSlides.map(mockToHeroApi));
      setUsingMock(true);
    } else {
      setSlides(result.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  const handleEdit = (index: number) => {
    setEditing(index);
    setEditForm({ ...slides[index] });
  };

  const handleAdd = () => {
    setEditing(-1);
    setEditForm({
      id: 0,
      tag: '',
      title: '',
      subtitle: '',
      cta_primary: '',
      cta_secondary: '',
      image: '',
      sort_order: slides.length + 1,
      active: 1,
    });
  };

  const saveEdit = async () => {
    // If editing a mock item (negative id), strip the id so the backend creates a new record
    const payload = {
      ...editForm,
      id: editForm.id > 0 ? editForm.id : undefined,
      sort_order: Number(editForm.sort_order) || 1,
      active: editForm.active === true || editForm.active === 1 ? 1 : 0,
    };
    setLoading(true);
    const res = await adminSaveHero(payload);
    setLoading(false);
    if (res.success && res.data) {
      Swal.fire({
        title: t('admin_api_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
        timer: 2000,
        timerProgressBar: true,
      });
      await loadSlides();
      setEditing(null);
    } else {
      Swal.fire({
        title: t('admin_api_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
  };

  const handleDelete = (index: number) => {
    const slide = slides[index];
    Swal.fire({
      title: `${t('admin_delete')} ${t('admin_slide')}?`,
      text: t('admin_delete_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_delete'),
      cancelButtonText: t('admin_cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const res = await adminDeleteHero(slide.id);
        setLoading(false);
        if (res.success) {
          Swal.fire({
            title: t('admin_api_deleted'),
            icon: 'success',
            confirmButtonColor: '#3DBFA0',
            timer: 1500,
          });
          await loadSlides();
        } else {
          Swal.fire({
            title: t('admin_api_error'),
            text: res.message || '',
            icon: 'error',
            confirmButtonColor: '#3DBFA0',
            confirmButtonText: t('admin_ok'),
          });
        }
      }
    });
  };

  const handleUploadImage = (isEditForm = false) => {
    fileInputRef.current?.click();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__heroUploadTarget = isEditForm ? 'edit' : null;
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setLoading(true);
    const res = await adminUploadHeroImage(formData);
    setLoading(false);
    if (res.success && (res.data?.url || res.data?.path)) {
      const uploadedUrl = res.data.url || res.data.path || '';
      Swal.fire({
        title: t('admin_upload_success'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
        timer: 1500,
      });
      setEditForm((prev) => ({ ...prev, image: uploadedUrl }));
    } else {
      Swal.fire({
        title: t('admin_upload_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelected}
      />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">
            {t('admin_hero_manager')}
          </h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_manage_hero')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadSlides}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-gray-200 text-brand-muted text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-sm"
          >
            <i className="ri-add-line" />
            {t('admin_add_slide')}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="py-16 text-center">
          <div className="inline-flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-3xl text-brand-mint" />
            <p className="text-sm text-brand-muted">{t('admin_loading_data')}</p>
          </div>
        </div>
      )}

      {/* Default / Offline banner */}
      {!loading && usingMock && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
            <i className="ri-information-line text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {fetchError ? 'Backend not reachable' : 'Default hero slides loaded'}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {fetchError
                ? `Showing default slides from the landing page. (${fetchError})`
                : 'These slides are displayed on the public site but are not saved in the backend yet. Edit and save them here to enable full management.'}
            </p>
          </div>
          <button
            onClick={loadSlides}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      )}

      {/* True empty state — backend reachable but 0 slides (not using mock) */}
      {!loading && !usingMock && slides.length === 0 && (
        <div className="py-16 bg-white rounded-xl border border-gray-100 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-light/60 flex items-center justify-center mx-auto mb-4">
            <i className="ri-image-line text-brand-muted text-3xl" />
          </div>
          <h3 className="text-base font-semibold text-brand-dark">{t('admin_no_slides')}</h3>
          <p className="text-sm text-brand-muted mt-1 mb-5">{t('admin_add_first_slide')}</p>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer shadow-sm"
          >
            <i className="ri-add-line" />
            {t('admin_add_slide')}
          </button>
        </div>
      )}

      {/* Slides List */}
      {!loading && slides.length > 0 && (
        <div className="space-y-4">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-brand-mint/20 transition-colors"
            >
              <div className="flex flex-col md:flex-row">
                <div className="md:w-56 h-36 md:h-auto shrink-0 relative">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  {!normalizeActive(slide.active) && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="px-3 py-1 bg-black/60 text-white text-xs font-bold rounded-full">
                        {t('admin_inactive')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-block px-2.5 py-1 text-[11px] font-bold tracking-wider uppercase text-brand-mint bg-brand-mint/10 rounded-md">
                        {slide.tag}
                      </span>
                      <span className="text-xs text-brand-muted">
                        {t('admin_slide_order')} #{slide.sort_order}
                      </span>
                      <span
                        className={`ml-auto inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          normalizeActive(slide.active)
                            ? 'bg-green-50 text-green-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        <i className={normalizeActive(slide.active) ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} />
                        {normalizeActive(slide.active) ? t('admin_active') : t('admin_inactive')}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-brand-dark font-heading">
                      {slide.title}
                    </h3>
                    <p className="text-sm text-brand-muted mt-1.5 line-clamp-2">
                      {slide.subtitle}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-brand-muted">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-hand-coin-line" />
                        {slide.cta_primary}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line" />
                        {slide.cta_secondary}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={() => handleEdit(index)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-brand-dark hover:bg-brand-light rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line" />
                      {t('admin_edit')}
                    </button>
                    <button
                      onClick={() => handleUploadImage(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-brand-mint hover:bg-brand-mint/10 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-upload-cloud-2-line" />
                      {t('admin_upload_image_title')}
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line" />
                      {t('admin_delete')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Add Modal */}
      {editing !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-brand-dark/40 backdrop-blur-sm"
            onClick={() => setEditing(null)}
          />
          <div className="relative bg-white rounded-2xl border border-gray-100 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-brand-dark font-heading">
                  {editing === -1 ? t('admin_add') : t('admin_edit')} {t('admin_slide')}
                </h3>
                <p className="text-xs text-brand-muted">{t('admin_manage_hero')}</p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-brand-dark" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${normalizeActive(editForm.active) ? 'bg-green-100' : 'bg-gray-200'}`}
                  >
                    <i
                      className={`text-lg ${normalizeActive(editForm.active) ? 'ri-eye-line text-green-600' : 'ri-eye-off-line text-gray-500'}`}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-brand-dark">
                      {normalizeActive(editForm.active) ? t('admin_active') : t('admin_inactive')}
                    </p>
                    <p className="text-xs text-brand-muted">
                      {normalizeActive(editForm.active) ? t('admin_visible') : t('admin_hidden')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setEditForm({
                      ...editForm,
                      active: normalizeActive(editForm.active) ? 0 : 1,
                    })
                  }
                  className={`relative w-12 h-7 rounded-full transition-colors duration-200 cursor-pointer ${normalizeActive(editForm.active) ? 'bg-brand-mint' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform duration-200 ${normalizeActive(editForm.active) ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-dark mb-2">
                  {t('admin_preview_image')}
                </label>
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={editForm.image}
                    alt={editForm.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-brand-dark/0 hover:bg-brand-dark/30 transition-colors">
                    <button
                      onClick={() => handleUploadImage(true)}
                      className="opacity-0 hover:opacity-100 inline-flex items-center gap-2 px-4 py-2 bg-white text-brand-dark text-sm font-medium rounded-lg shadow-lg transition-opacity cursor-pointer"
                    >
                      <i className="ri-upload-cloud-2-line" />
                      {t('admin_change_image')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_slide_tag')}
                  </label>
                  <input
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.tag}
                    onChange={(e) => setEditForm({ ...editForm, tag: e.target.value })}
                    placeholder={t('admin_tag_placeholder')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_slide_order')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.sort_order}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sort_order: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                  {t('admin_slide_title')}
                </label>
                <input
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder={t('admin_enter_title')}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                  {t('admin_slide_subtitle')}
                </label>
                <textarea
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint resize-none transition-colors"
                  rows={3}
                  value={editForm.subtitle}
                  onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
                  placeholder={t('admin_enter_desc')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_primary_btn')}
                  </label>
                  <input
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.cta_primary}
                    onChange={(e) => setEditForm({ ...editForm, cta_primary: e.target.value })}
                    placeholder={t('hero_cta_primary')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_secondary_btn')}
                  </label>
                  <input
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.cta_secondary}
                    onChange={(e) => setEditForm({ ...editForm, cta_secondary: e.target.value })}
                    placeholder={t('hero_cta_secondary')}
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
              <span className="text-xs text-brand-muted">{t('admin_save_to_backend')}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 bg-gray-100 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {t('admin_cancel')}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer shadow-sm disabled:opacity-60"
                >
                  {loading ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-save-line" />
                  )}
                  {t('admin_save_slide')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}